import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core'
import { default as MUITextField } from '@material-ui/core/TextField'
import { Search } from '@material-ui/icons'
import { cloneElement, useState } from 'react'
import {
  BooleanInput,
  Button,
  Datagrid,
  ExportButton,
  Filter,
  FunctionField,
  NumberField,
  ReferenceField,
  sanitizeListRestProps,
  TextField,
  TextInput,
  TopToolbar,
  useNotify,
  useRedirect,
} from 'react-admin'

import { captureExceptionIfProduction } from 'helpers/utils'
import { getCTXLoad } from '../../api/CTXLoads'
import { startFeasibilityCheck } from '../../api/schedules'
import { CustomBooleanField, CustomDateField, FullNameField } from '../Fields'
import CTList from '../List'

const generateFlags = (record: any) => {
  return (
    <>
      <strong>Origin:</strong>
      <ul>
        {record.origin_flags.map((item: any) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <strong>Destination:</strong>
      <ul>
        {record.destination_flags.map((item: any) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </>
  )
}

const FeasibilityStatFilter = (props: any) => (
  <Filter {...props}>
    <TextInput label="Search" source="search" alwaysOn />
    <BooleanInput label="Feasible Loads Only" source="is_feasible" alwaysOn />
  </Filter>
)

const FindCTXLoadButton = () => {
  const redirect = useRedirect()
  const [isSubmitLoading, setSubmitLoading] = useState(false)
  const [CTXLoadLoading, setCTXLoadLoading] = useState(false)
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [refId, setRefId] = useState('')
  const notify = useNotify()

  return (
    <>
      <Button label="Check by CTX Ref ID" basePath="ctx-loads" onClick={() => setDialogOpen(true)}>
        <Search />
      </Button>
      <Dialog open={isDialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Check by CTX Reference ID</DialogTitle>
        <DialogContent>
          {isSubmitLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="20vh">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box>
                <DialogContentText>
                  Enter the reference id (e.g. "UEXR7Z") of a CT Exchange load below to start driver
                  feasibility report generation.
                </DialogContentText>
                <MUITextField
                  label="Reference ID"
                  variant="outlined"
                  onChange={(event) => setRefId(event.target.value)}
                  disabled={CTXLoadLoading}
                  required
                  fullWidth
                />
                <DialogContentText>
                  <ul>
                    <li>
                      <small>
                        Clicking on the button will start the report generation in the background and apply a
                        filter to show the results for this execution only.
                      </small>
                    </li>
                    <li>
                      <small>
                        If you don't see results at first, please use the refresh button (top right).
                      </small>
                    </li>
                  </ul>
                </DialogContentText>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} label="Cancel" disabled={isSubmitLoading} />
          <Button
            variant="contained"
            disabled={CTXLoadLoading || isSubmitLoading}
            onClick={async () => {
              try {
                setSubmitLoading(true)
                setCTXLoadLoading(true)
                const result = await getCTXLoad(refId)
                setCTXLoadLoading(false)
                if (result?.length) {
                  const feasibilityCheckResponse = await startFeasibilityCheck('ctx_load', result[0].id)
                  setTimeout(() => {
                    setSubmitLoading(false)
                    setDialogOpen(false)
                    redirect(`/feasibility-stat?filter={"load":"${feasibilityCheckResponse.load_id}"}`)
                  }, 3000)
                } else {
                  throw new Error('Ref ID not found')
                }
              } catch (error: any) {
                setDialogOpen(false)
                setSubmitLoading(false)
                notify(`Oops, something went wrong: ${error.message}`, { type: 'error' })
                captureExceptionIfProduction(error)
              }
            }}
            label={isSubmitLoading ? 'Redirecting...' : 'Check'}
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

const FeasibilityStatActions = ({
  currentSort,
  className,
  resource,
  exporter,
  filterValues,
  permanentFilter,
  maxResults,
  total,
  showFilter,
  displayedFilters,
  filters,
  ...rest
}: any) => (
  <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
    <FindCTXLoadButton />
    {filters &&
      cloneElement(filters, {
        resource,
        showFilter,
        displayedFilters,
        filterValues,
        context: 'button',
      })}
    <ExportButton
      disabled={total === 0}
      resource={resource}
      sort={currentSort}
      filterValues={filterValues}
      exporter={exporter}
      maxResults={maxResults}
    />
  </TopToolbar>
)

export const FeasibilityStatList = (props: any) => (
  <CTList
    bulkActionButtons={false}
    filters={<FeasibilityStatFilter />}
    title="Feasibility Stats Dashboard"
    actions={<FeasibilityStatActions />}
    {...props}
  >
    <Datagrid optimized>
      <ReferenceField record={props.record} source="load" reference="loads" link="show">
        <TextField source="id" label="Load ID" />
      </ReferenceField>
      <CustomDateField source="target_pickup_time" label="Target pick-up time" format="MM/dd/yyyy HH:mm" />
      <CustomBooleanField source="is_feasible" label="Feasible" />

      <ReferenceField record={props.record} source="user_id" reference="users" link="show">
        <FullNameField />
      </ReferenceField>

      <TextField source="user_email" label="User E-mail" />
      <TextField source="user_phone" label="User Phone" />
      <CustomDateField source="eta_at_pickup" label="ETA at pick-up" format="MM/dd/yyyy HH:mm" />
      <TextField source="previous_job" label="Previous Job" />
      <TextField source="previous_job_delivery_address" label="Previous Job Delivery Address" />
      <CustomDateField
        source="previous_job_delivery_time"
        label="Previous Job Delivery Time"
        format="MM/dd/yyyy HH:mm"
      />

      <NumberField
        label="Hours (prev delivery appointment to pick-up appt)"
        source="hours_previous_job_delivery_to_target_pickup"
      />
      <TextField source="deadhead_to_pickup" label="Deadhead mi (prev delivery to pick-up)" />
      <FunctionField label="Flags" render={(record: any) => generateFlags(record)} />
    </Datagrid>
  </CTList>
)
